// @flow
import { useEffect } from 'react';
import Debug from 'debug';
import OlSelect from 'ol/interaction/Select.js';
import CollectionEventType from 'ol/CollectionEventType';
import type { Collection, SelectInteraction, SelectEvent, Style, StyleFunction } from '../types';
import { useMap, useFeatureListeners } from '../context';
import { useLayer } from '../layers/context';

const debug = Debug('amms:modules:openlayers:interactions:select');

type Props = $ReadOnly<{
    label?: string,
    condition?: {||},
    features?: Collection,
    multi?: boolean,
    style?: Style | StyleFunction,

    onSelect?: (e: SelectEvent) => void
}>;

function Select({ label = '', condition, features, multi, style, onSelect }: Props) {
    const map = useMap();
    const layer = useLayer();
    const featureListeners = useFeatureListeners();

    useEffect(() => {
        let select: SelectInteraction;
        let _onSelect;
        let _onAdd;
        let _onRemove;
        let featuresCollection;
        if(map) {
            if(layer) {
                debug(`Adding select interaction ${label} to layer: `, layer);
            }
            else {
                debug(`Adding select interaction ${label} to map`);
            }

            select = new OlSelect({
                condition,
                features,
                multi,
                style,
                layers: layer ? [layer] : undefined
            });
            select.set('label', label);

            const originalHandleEvent = select.handleEvent;
            select.handleEvent = function() {
                originalHandleEvent.apply(this, arguments);

                // true, not to stop event propagation.
                return true;
            };

            map.addInteraction(select);

            const handleSelect = (e, selectedFeatures, deselectedFeatures) => {
                const listeners = featureListeners.select;

                if(listeners) {
                    let layerFeatures;
                    if(layer) {
                        const source = layer.getSource();
                        layerFeatures = source ? source.getFeatures() : [];
                    }

                    const filter = (feature) => !layerFeatures || layerFeatures.indexOf(feature) !== -1;

                    if(deselectedFeatures) {
                        for(let i = 0, length = deselectedFeatures.length; i < length; i += 1) {
                            const feature = deselectedFeatures[i];
                            const listener = listeners.get(feature);

                            if(listener && filter(feature)) {
                                listener(false);
                            }
                        }
                    }

                    if(selectedFeatures) {
                        for(let i = 0, length = selectedFeatures.length; i < length; i += 1) {
                            const feature = selectedFeatures[i];
                            const listener = listeners.get(feature);

                            if(listener && filter(feature)) {
                                listener(true);
                            }
                        }
                    }
                }

                if(onSelect) {
                    onSelect(e);
                }
            };

            _onSelect = (e) => {
                const selectedFeatures = e.target.getFeatures().getArray();
                const deselectedFeatures = e.deselected;

                handleSelect(e, selectedFeatures, deselectedFeatures);
            };
            select.on('select', _onSelect);

            _onAdd = (e) => {
                handleSelect(e, [e.element]);
            };
            _onRemove = (e) => {
                handleSelect(e, null, [e.element]);
            };
            featuresCollection = select.getFeatures();
            featuresCollection.on('add', _onAdd);
            featuresCollection.on('remove', _onRemove);
        }

        return () => {
            if(map && select) {
                if(layer) {
                    debug(`Removing select interaction ${label} from layer: `, layer);
                }
                else {
                    debug(`Removing select interaction ${label} from map`);
                }

                if(features) {
                    const addListeners = features.getListeners(CollectionEventType.ADD);
                    const removeListeners = features.getListeners(CollectionEventType.REMOVE);

                    // We have to do this dirty hack, because class Select isn't removing event listeners from features collection
                    if(addListeners[0]) {
                        features.removeEventListener(CollectionEventType.ADD, addListeners[0]);
                    }
                    if(removeListeners[0]) {
                        features.removeEventListener(CollectionEventType.REMOVE, removeListeners[0]);
                    }
                }

                select.un('select', _onSelect);

                if(featuresCollection) {
                    featuresCollection.un('add', _onAdd);
                    featuresCollection.un('remove', _onRemove);
                }

                map.removeInteraction(select);
            }
        };
    }, [label, map, layer, condition, features, multi, featureListeners, style, onSelect]);

    return null;
}

export default Select;